import { __awaiter } from "tslib";
import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getAvailableInsuranceCodes, getOrder as getCheckoutOrder, getPriorityInsuranceProgram, getSelectedInsuranceCodes } from '../../../Checkout/store/order/selectors';
import { getCheckinOrder } from '../../../Modules/Checkin/store/order/selectors';
import { useToggleable } from '../../../hooks';
import { useInsuranceUpsaleConfig } from '../../hooks';
import { InsuranceGoal, reachGoal } from '../../../analytics';
import { getFunnelParameters } from '../../../DataLayerAnalytics/utils';
import { Mode } from '../../../types';
import { ModeContext } from '../../../context';
export const useInsuranceUpsaleModal = ({ strategy, availableInsuranceCodes, selectedInsuranceCodes }) => {
    const mode = useContext(ModeContext);
    const order = mode === Mode.Checkin ? useSelector(getCheckinOrder) : useSelector(getCheckoutOrder);
    const insuranceAvailableCodes = useSelector(getAvailableInsuranceCodes);
    const insuranceSelectedCodes = useSelector(getSelectedInsuranceCodes);
    const priorityInsurance = useSelector(getPriorityInsuranceProgram);
    const { isOpen: isUpsaleOpen, setOpen: setUpsaleOpen, open: openUpsaleModal, close: closeUpsaleModal } = useToggleable(false);
    const insuranceUpsaleConfig = useInsuranceUpsaleConfig();
    const availableCodes = useMemo(() => {
        return availableInsuranceCodes !== null && availableInsuranceCodes !== void 0 ? availableInsuranceCodes : insuranceAvailableCodes;
    }, [availableInsuranceCodes, insuranceAvailableCodes]);
    const selectedCodes = useMemo(() => {
        return selectedInsuranceCodes !== null && selectedInsuranceCodes !== void 0 ? selectedInsuranceCodes : insuranceSelectedCodes;
    }, [selectedInsuranceCodes !== null && selectedInsuranceCodes !== void 0 ? selectedInsuranceCodes : insuranceSelectedCodes]);
    const isNeedToShowInsuranceUpsale = useMemo(() => {
        if (!priorityInsurance) {
            return false;
        }
        if (!insuranceUpsaleConfig.getIsNeedToShow(strategy)) {
            return false;
        }
        // if the priority insurance is not present in the list, then we skip the step
        if (!availableCodes.includes(priorityInsurance.code)) {
            return false;
        }
        return !selectedCodes.includes(priorityInsurance.code);
    }, [strategy, priorityInsurance, availableCodes, selectedCodes, insuranceUpsaleConfig.getIsNeedToShow]);
    const analyticsFunnelPurchase = useMemo(() => getFunnelParameters(order), [order]);
    const onCloseUpsaleModal = useCallback(() => {
        insuranceUpsaleConfig.onApplyStrategy(strategy);
        closeUpsaleModal();
    }, [strategy, insuranceUpsaleConfig.onApplyStrategy, closeUpsaleModal]);
    const onAgree = useCallback((callback) => __awaiter(void 0, void 0, void 0, function* () {
        reachGoal(InsuranceGoal.InsurancePopupYes, { funnel_purchase: analyticsFunnelPurchase });
        if (priorityInsurance) {
            // if the priority insurance is present in the list, then we skip the request for addition
            if (selectedCodes.includes(priorityInsurance.code)) {
                return;
            }
            onCloseUpsaleModal();
            yield (callback === null || callback === void 0 ? void 0 : callback(priorityInsurance.code ? [priorityInsurance.code] : undefined));
            return;
        }
        onCloseUpsaleModal();
    }), [priorityInsurance, selectedCodes, onCloseUpsaleModal]);
    const onDisagree = useCallback((callback) => __awaiter(void 0, void 0, void 0, function* () {
        reachGoal(InsuranceGoal.InsurancePopupNo, { funnel_purchase: analyticsFunnelPurchase });
        onCloseUpsaleModal();
        yield (callback === null || callback === void 0 ? void 0 : callback());
    }), [onCloseUpsaleModal]);
    const onContinue = useCallback((callback) => __awaiter(void 0, void 0, void 0, function* () {
        if (isNeedToShowInsuranceUpsale) {
            openUpsaleModal();
            return;
        }
        yield callback();
    }), [openUpsaleModal, isNeedToShowInsuranceUpsale]);
    return {
        // data
        priorityInsurance,
        isUpsaleOpen,
        isNeedToShowInsuranceUpsale,
        // methods
        setUpsaleOpen,
        onCloseUpsaleModal,
        onAgree,
        onDisagree,
        onContinue
    };
};
